<template>
  <div class="bg-slate-50 rounded-2xl p-4 border border-slate-200">
    <p class="text-slate-700 text-sm font-medium mb-1">העלו תמונה / PDF של רשימת השירותים שלכם</p>
    <p class="text-[11px] text-slate-400 mb-3">נחלץ אוטומטית את השמות, המחירים והתיאורים. אפשר להעלות כמה קבצים.</p>

    <!-- Selected files -->
    <div v-if="files.length" class="flex flex-col gap-2 mb-3">
      <div
        v-for="(file, i) in files"
        :key="i"
        class="flex items-center gap-3 bg-white border border-slate-200 rounded-xl px-3 py-2"
      >
        <div class="w-9 h-9 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center flex-shrink-0">
          <img v-if="previews[i]" :src="previews[i]" class="w-full h-full object-cover" alt="" />
          <FileText v-else class="w-4 h-4 text-slate-400" />
        </div>
        <span class="text-xs text-slate-600 truncate flex-1">{{ file.name }}</span>
        <button
          type="button"
          class="text-slate-400 hover:text-pink-500 transition-colors"
          @click="removeFile(i)"
        >
          <Trash2 class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Upload area -->
    <label
      :for="inputId"
      class="flex flex-col items-center justify-center w-full h-28 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-white hover:bg-slate-50 transition-colors"
    >
      <div class="flex flex-col items-center gap-2">
        <ImagePlus class="w-6 h-6 text-slate-400" />
        <span class="text-xs text-slate-500">{{ files.length ? 'הוספת קובץ נוסף' : 'לחצו להעלאת רשימת שירותים' }}</span>
        <span class="text-[10px] text-slate-400">PNG, JPG, WEBP, PDF</span>
      </div>
      <input
        :id="inputId"
        type="file"
        class="hidden"
        accept="image/png, image/jpeg, image/webp, image/heic, application/pdf"
        multiple
        @change="onFilesSelected"
      />
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { ImagePlus, Trash2, FileText } from 'lucide-vue-next';
import { generateRandomId } from '@app/utils/strings';

const props = defineProps<{
  modelValue: File[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: File[]];
}>();

const inputId = generateRandomId(10);
const files = ref<File[]>([...props.modelValue]);
const previews = ref<(string | null)[]>([]);

const rebuildPreviews = () => {
  previews.value = files.value.map(f =>
    f.type.startsWith('image/') ? URL.createObjectURL(f) : null
  );
};

watch(
  () => props.modelValue,
  (val) => {
    if (val !== files.value) {
      files.value = [...val];
      rebuildPreviews();
    }
  }
);

const onFilesSelected = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selected = Array.from(target.files ?? []);
  if (selected.length) {
    files.value = [...files.value, ...selected];
    rebuildPreviews();
    emit('update:modelValue', files.value);
  }
  target.value = '';
};

const removeFile = (index: number) => {
  files.value.splice(index, 1);
  rebuildPreviews();
  emit('update:modelValue', files.value);
};
</script>
